import * as React from "react";
import { Query, Mutation } from "react-apollo";
import { Row, Col, Form, Label, FormGroup, Input, Button } from "reactstrap";
import Select from "react-select";
import * as _ from "lodash";
import TimeField from "react-simple-timefield";
import * as moment from "moment";
import * as momentRecur from "moment-recur";

// GraphQL
import CreateWorkOrderMutation from "../../GraphQL/Mutations/CreateWorkOrder";
import GetCompanySites from "../../GraphQL/Queries/GetCompanySites";
import GetCompanyTechnicians from "../../GraphQL/Queries/GetCompanyTechnicians";

// React Datepicker
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";

import { DateRangePicker, SingleDatePicker } from "react-dates";

// Presentation/UI
import Loader from "../../Components/Loader";
import FormCheckbox from "../../Components/FormElements/FormCheckbox";

// Theme
import { Colors } from "../../Themes";
import GetCompanyWorkOrders from "../../GraphQL/Queries/GetCompanyWorkOrders";

// Utils
import { PRIORITIES, RECURRENCE } from "../../Utils/Consts";

interface IProps {
  companyId: any;
  closeModal(): void;
  notification(message: string): void;
}

interface IState {
  title: string;
  time: any;
  description: string;
  dueDate: any;
  startDate: any;
  endDate: any;
  interval: any;
  recurrence: any;
  priority: any;
  loading: boolean;
  showRecurrenceForm: boolean;
  focused: any;
  focusedInput: any;
  siteId: any;
  userId: any;
  additionalWorkers: any;
  additionalWorkerIds: any;
  error: null | string;
}

const TimeFieldStyling = {
  background: "rgba(214, 219, 230, 0.36) !important",
  color: "#343951 !important",
  border: "none !important",
  fontSize: "12px !important"
};

class AddWorkOrderForm extends React.Component<IProps, IState> {
  state = {
    title: "",
    time: "",
    description: "",
    dueDate: moment(),
    startDate: moment(),
    endDate: moment(),
    interval: "1",
    recurrence: { value: "", label: "" },
    priority: { value: "", label: "" },
    loading: false,
    focused: false,
    focusedInput: null,
    showRecurrenceForm: false,
    siteId: null,
    userId: null,
    additionalWorkers: [],
    additionalWorkerIds: [],
    error: null
  };

  // Handle change
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "title":
        this.setState({ title: value });
        break;
      case "description":
        this.setState({ description: value });
        break;
      case "interval":
        this.setState({ interval: value });
        break;
      default:
        break;
    }
  };

  // Select site
  selectSite = (site: any) => {
    const siteId = site.value.id;
    this.setState({ siteId });
  };

  // Return sites
  returnSites = (
    sites: Array<any>
  ): Array<{ value: object; label: string }> => {
    return sites.map((site: any) => {
      return {
        value: site,
        label: `${site.name}`
      };
    });
  };

  // Select users
  selectUser = (user: any) => {
    const userId = user.value.id;
    this.setState({ userId });
  };

  // Return users
  returnUsers = (
    users: Array<any>
  ): Array<{ value: object; label: string }> => {
    return users.map((user: any) => {
      return {
        value: user,
        label: `${user.firstName} ${user.lastName}`
      };
    });
  };

  // Return array of selected employee ids
  getSelectedEmployees = () => {
    return this.state.additionalWorkers.map((employee: any) => {
      return employee.value.id;
    });
  };

  // Select additional workers
  selectAdditionalWorkers = (workers: any): void => {
    workers = _.uniqBy(workers, "id");
    this.setState({ additionalWorkers: workers });
  };

  validateForm = (): boolean => {
    const { title, priority } = this.state;
    // Check for undefined or empty infput fields
    if (!title || !priority) {
      this.setError("Please fill in the title and priority of the work order");
      return false;
    }
    return true;
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  // Select priority
  selectPriority = (priority: any) => {
    this.setState({ priority });
  };

  // Select recurrence
  selectRecurrence = (recurrence: any) => {
    this.setState({ recurrence });
  };

  // Toggle recurrence form
  toggleRecurrenceForm = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ showRecurrenceForm: !this.state.showRecurrenceForm });
  };

  // change the time for a work order
  onTimeChange = (time: string): void => {
    this.setState({ time });
  };

  // Get recurring dates
  returnRecurringDates = (
    startDate: any,
    endDate: any,
    interval: number | string,
    recurrence: string
  ): Array<any> => {
    if (
      !startDate ||
      !endDate ||
      !interval ||
      interval === 0 ||
      !recurrence ||
      recurrence === ""
    ) {
      return [];
    }

    let recur: any;

    switch (recurrence) {
      case "day":
        recur = momentRecur()
          .recur(
            moment(startDate).format("MM/DD/YYYY"),
            moment(endDate).format("MM/DD/YYYY")
          )
          .every(interval)
          .days();
        break;
      case "weeks":
        recur = momentRecur()
          .recur(
            moment(startDate).format("MM/DD/YYYY"),
            moment(endDate).format("MM/DD/YYYY")
          )
          .every(interval)
          .weeks();
        break;
      case "months":
        recur = momentRecur()
          .recur(
            moment(startDate).format("MM/DD/YYYY"),
            moment(endDate).format("MM/DD/YYYY")
          )
          .every(interval)
          .months();
        break;
      default:
        recur = [];
    }

    if (!recur) {
      return [];
    }

    return recur.all("L");
  };

  render() {
    const {
      title,
      time,
      description,
      dueDate,
      startDate,
      endDate,
      interval,
      recurrence,
      priority,
      showRecurrenceForm,
      siteId,
      userId
    } = this.state;

    const recurrenceDates = this.returnRecurringDates(
      startDate,
      endDate,
      interval,
      recurrence.value
    );

    const { companyId, closeModal, notification } = this.props;
    return (
      <Query query={GetCompanySites} variables={{ id: companyId }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Loader />;
          }
          if (error) {
            return <div>There was a problem loading the data</div>;
          }

          const sites = data.allSites;

          return (
            <Query query={GetCompanyTechnicians} variables={{ id: companyId }}>
              {usersQuery => {
                if (usersQuery.loading) {
                  return <Loader />;
                }
                if (usersQuery.error) {
                  return <div>There was a problem loading the data</div>;
                }

                const users = usersQuery.data.allUsers;

                return (
                  <Mutation<{}, {}> mutation={CreateWorkOrderMutation}>
                    {createWorkOrder => (
                      <Form
                        onSubmit={e => {
                          e.preventDefault();
                          if (this.validateForm()) {
                            this.setState({ loading: true });

                            createWorkOrder({
                              variables: {
                                title,
                                description,
                                time,
                                interval,
                                isRecurring: showRecurrenceForm,
                                recurrence: recurrence.value,
                                recurrenceDates,
                                startDate: moment(startDate).format(
                                  "DD/MM/YYYY"
                                ),
                                endDate: moment(endDate).format("DD/MM/YYYY"),
                                dueDate: moment(dueDate).format("DD/MM/YYYY"),
                                priority: priority.value,
                                siteId,
                                assignedToId: userId,
                                additionalWorkersIds: this.getSelectedEmployees(),
                                status: "OPEN",
                                companyId
                              },
                              refetchQueries: [
                                {
                                  query: GetCompanyWorkOrders,
                                  variables: { id: companyId }
                                }
                              ]
                            })
                              .then(res => {
                                this.setState({ loading: false });

                                closeModal();
                                notification("Work order created successfully");
                              })
                              .catch(err => {
                                this.setState({ loading: false });
                              });
                          }
                        }}
                      >
                        <FormGroup>
                          <Label for="title">Title</Label>
                          <Input
                            type="text"
                            name="title"
                            id="title"
                            placeholder="Title"
                            onChange={this.handleChange}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="description">Description</Label>
                          <Input
                            type="textarea"
                            name="description"
                            id="description"
                            placeholder="Description"
                            onChange={this.handleChange}
                            style={{ resize: "none" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Priority</Label>
                          <Select
                            onChange={this.selectPriority}
                            options={PRIORITIES}
                            isSearchable={true}
                            placeholder="Priority"
                            className="select-styling"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Site</Label>
                          <Select
                            onChange={this.selectSite}
                            options={this.returnSites(sites)}
                            isSearchable={true}
                            placeholder="Sites"
                            className="select-styling"
                          />
                        </FormGroup>
                        <Row
                          style={{ marginLeft: "-15px", marginRight: "-15px" }}
                        >
                          <Col xs={12} md={6} lg={6}>
                            <FormGroup>
                              <Label>Date Scheduled For</Label>
                              <br />
                              <SingleDatePicker
                                date={dueDate} // momentPropTypes.momentObj or null
                                onDateChange={date =>
                                  this.setState({ dueDate: date })
                                }
                                focused={this.state.focused}
                                onFocusChange={({ focused }) =>
                                  this.setState({ focused })
                                }
                                id="date_scheduled_for_id"
                                hideKeyboardShortcutsPanel={true}
                                placeholder="Date Scheduled For"
                              />
                            </FormGroup>
                          </Col>
                          <Col xs={12} md={6} lg={6}>
                            <FormGroup>
                              <Label>Enter Time</Label>
                              <br />
                              <TimeField
                                style={TimeFieldStyling}
                                input={
                                  <input type="text" className="form-control" />
                                }
                                value={time}
                                onChange={this.onTimeChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <FormGroup>
                          <Label>Technician</Label>
                          <Select
                            onChange={this.selectUser}
                            options={this.returnUsers(users)}
                            isSearchable={true}
                            placeholder="Users"
                            className="select-styling"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Additional Workers</Label>
                          <Select
                            onChange={this.selectAdditionalWorkers}
                            options={this.returnUsers(users)}
                            isMulti={true}
                            isSearchable={true}
                            placeholder="Additional workers"
                            className="select-styling"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Is the work order recurring?</Label>
                          <FormCheckbox
                            name="showRecurrenceForm"
                            checked={showRecurrenceForm}
                            label={!showRecurrenceForm ? "No" : "Yes"}
                            htmlFor={`showRecurrenceForm`}
                            inlineStyling={{
                              paddingLeft: "0px",
                              paddingRight: "0px",
                              paddingTop: "5px",
                              paddingBottom: "5px"
                            }}
                            onChange={(e: React.FormEvent<HTMLInputElement>) =>
                              this.toggleRecurrenceForm(e)
                            }
                          />
                        </FormGroup>
                        {showRecurrenceForm && (
                          <Row
                            style={{
                              marginLeft: "-15px",
                              marginRight: "-15px"
                            }}
                          >
                            <Col xs={12} md={12} lg={12}>
                              <FormGroup>
                                <DateRangePicker
                                  startDate={startDate} // momentPropTypes.momentObj or null,
                                  startDateId="work_order_start_date_id" // PropTypes.string.isRequired,
                                  endDate={endDate} // momentPropTypes.momentObj or null,
                                  endDateId="work_order_end_date_id" // PropTypes.string.isRequired,
                                  isOutsideRange={() => false}
                                  onDatesChange={(date: any) =>
                                    this.setState({
                                      startDate: date.startDate,
                                      endDate: date.endDate
                                    })
                                  } // PropTypes.func.isRequired,
                                  focusedInput={this.state.focusedInput} // PropTypes.oneOf([START_DATE, END_DATE]) or null,
                                  onFocusChange={focusedInput =>
                                    this.setState({ focusedInput })
                                  } // PropTypes.func.isRequired,
                                  hideKeyboardShortcutsPanel={true}
                                />
                              </FormGroup>
                            </Col>
                            <Col xs={12} md={6} lg={6}>
                              <FormGroup>
                                <Label>Interval</Label>
                                <Input
                                  type="number"
                                  name="interval"
                                  id="interval"
                                  placeholder="Interval"
                                  value={interval}
                                  onChange={this.handleChange}
                                />
                              </FormGroup>
                            </Col>
                            <Col xs={12} md={6} lg={6}>
                              <FormGroup>
                                <Label>Recurrence</Label>
                                <Select
                                  onChange={this.selectRecurrence}
                                  options={RECURRENCE}
                                  isSearchable={true}
                                  placeholder="Recurrence"
                                  className="select-styling"
                                />
                              </FormGroup>
                            </Col>
                            <Col xs={12} md={12} lg={12}>
                              Work order to recur every {interval}{" "}
                              {recurrence.value} at {time} for the selected date
                              range/period.
                            </Col>
                          </Row>
                        )}
                        <br />
                        <FormGroup>
                          <Button>
                            {!this.state.loading ? (
                              <span>Save</span>
                            ) : (
                              <Loader />
                            )}
                          </Button>
                        </FormGroup>
                        {this.state.error && (
                          <p
                            style={{ textAlign: "center", color: Colors.error }}
                          >
                            {this.state.error}
                          </p>
                        )}
                      </Form>
                    )}
                  </Mutation>
                );
              }}
            </Query>
          );
        }}
      </Query>
    );
  }
}

export default AddWorkOrderForm;



// WEBPACK FOOTER //
// ./src/Containers/WorkOrders/AddWorkOrderForm.tsx